const OPEN_KEY = "hydrotools:global-layers:open:v1";
const LAYER_TYPE_ORDER = [
  "Basemaps",
  "Overlays",
  "ENC",
  "Linework",
  "Line Planning",
  "Soundings",
  "Other",
];

function esc(v) {
  return String(v ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll("\"", "&quot;");
}

function tracklineStyleControls(layer) {
  if (String(layer?.meta?.sourceType || "") !== "qimera-tracklines") return "";
  const color = String(layer?.meta?.tracklineColor || "#2dd4bf").trim() || "#2dd4bf";
  const weight = Math.max(1, Math.min(5, Number(layer?.meta?.tracklineWeight) || 3));
  const lineType = String(layer?.meta?.tracklineLineType || "solid").trim() || "solid";
  const colorMode = String(layer?.meta?.tracklineColorMode || "solid").trim() || "solid";
  return `
    <div class="trackline-style-controls">
      <select
        class="trackline-style-mode"
        title="Trackline color mode"
        aria-label="Trackline color mode"
        data-act="trackline-color-mode"
        data-id="${esc(layer.id)}"
      >
        <option value="solid"${colorMode === "solid" ? " selected" : ""}>Solid</option>
        <option value="nadir-depth"${colorMode === "nadir-depth" ? " selected" : ""}>Depth</option>
        <option value="surface-sv"${colorMode === "surface-sv" ? " selected" : ""}>Surface SV</option>
        <option value="time"${colorMode === "time" ? " selected" : ""}>Time</option>
        <option value="svp-profile"${colorMode === "svp-profile" ? " selected" : ""}>SVP</option>
      </select>
      <input
        class="trackline-style-color"
        type="color"
        title="Trackline color"
        aria-label="Trackline color"
        data-act="trackline-color"
        data-id="${esc(layer.id)}"
        value="${esc(color)}"
      />
      <details class="trackline-style-menu" data-trackline-menu="weight">
        <summary
          class="trackline-style-summary"
          title="Trackline weight"
          aria-label="Trackline weight"
        >
          <span class="trackline-style-chip-line trackline-style-chip-line-weight" style="--trackline-preview-height:${Math.max(2, Math.min(10, weight * 2))}px;"></span>
          <span class="trackline-style-summary-caret">⌄</span>
        </summary>
        <div class="trackline-style-popover" role="listbox" aria-label="Trackline weight options">
          ${[1, 2, 3, 4, 5].map((option) => `
            <button
              type="button"
              class="trackline-style-chip ${weight === option ? "active" : ""}"
              title="Weight ${option}"
              aria-label="Weight ${option}"
              data-act="trackline-weight-set"
              data-id="${esc(layer.id)}"
              data-value="${option}"
            >
              <span class="trackline-style-chip-line trackline-style-chip-line-weight" style="--trackline-preview-height:${Math.max(2, Math.min(10, option * 2))}px;"></span>
            </button>
          `).join("")}
        </div>
      </details>
      <details class="trackline-style-menu" data-trackline-menu="type">
        <summary
          class="trackline-style-summary"
          title="Trackline type"
          aria-label="Trackline type"
        >
          <span class="trackline-style-chip-line ${lineType === "dashed" ? "trackline-style-chip-line-dashed" : lineType === "dotted" ? "trackline-style-chip-line-dotted" : "trackline-style-chip-line-solid"}"></span>
          <span class="trackline-style-summary-caret">⌄</span>
        </summary>
        <div class="trackline-style-popover" role="listbox" aria-label="Trackline line type options">
          ${[
            ["solid", "trackline-style-chip-line-solid", "Solid"],
            ["dashed", "trackline-style-chip-line-dashed", "Dashed"],
            ["dotted", "trackline-style-chip-line-dotted", "Dotted"],
          ].map(([value, cls, label]) => `
            <button
              type="button"
              class="trackline-style-chip ${lineType === value ? "active" : ""}"
              title="${label}"
              aria-label="${label}"
              data-act="trackline-line-type-set"
              data-id="${esc(layer.id)}"
              data-value="${esc(value)}"
            >
              <span class="trackline-style-chip-line ${cls}"></span>
            </button>
          `).join("")}
        </div>
      </details>
    </div>
  `;
}

function classify(layer) {
  if (layer.kind === "basemap") return "Basemaps";
  if (layer.group) return String(layer.group);
  if (layer.kind === "overlay") return "Overlays";
  if (layer.toolId === "linework") return "Linework";
  if (layer.toolId === "line-planning") return "Line Planning";
  if (layer.toolId === "etopo-sounding") return "Soundings";
  return "Other";
}

function readOpenSet() {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeOpenSet(set) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify([...set]));
  } catch {}
}

function orderedTypes(grouped) {
  const out = [];
  const seen = new Set();
  LAYER_TYPE_ORDER.forEach((k) => {
    seen.add(k);
    out.push(k);
  });
  [...grouped.keys()]
    .filter((k) => !seen.has(k))
    .sort((a, b) => String(a).localeCompare(String(b)))
    .forEach((k) => out.push(k));
  return out;
}

export const globalLayersTool = {
  id: "global-layers",
  name: "Layers",
  init() {},
  destroy() {},
  renderPanel(app, containerEl) {
    const openSet = readOpenSet();
    const off = [];
    let firstRender = true;
    let activeSoundingsGlobalId = null;
    let unresolvedCount = 0;
    let dragLayerId = "";
    let dragLayerCat = "";
    let dragPlaceholderEl = null;
    let dragSourceEl = null;
    let dragGhostEl = null;
    let isLayerDragging = false;
    let pendingRender = false;
    let lastDropAnchorKey = "";
    let suppressDrag = false;

    function groupLayers() {
      const topToBottom = [...app.services.layers.listLayers()].reverse();
      const map = new Map();
      topToBottom.forEach((l) => {
        const key = classify(l);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(l);
      });
      return map;
    }

    function row(l) {
      const count = Number(l?.meta?.featureCount);
      const src = String(l?.meta?.source || "");
      const isSounding = l.toolId === "etopo-sounding" || String(l.group || "").toLowerCase() === "soundings";
      const hasLegend = l?.meta?.legendAvailable === true;
      const legendVisible = l?.meta?.legendVisible === true;
      return `
        <div class="layer-tree-row ${isSounding && activeSoundingsGlobalId === l.id ? "active" : ""}" draggable="true" data-layer-id="${esc(l.id)}" data-layer-cat="${esc(classify(l))}">
          <label class="layer-tree-vis" title="Visible">
            <input type="checkbox" data-act="vis" data-id="${esc(l.id)}" ${l.visible ? "checked" : ""} />
          </label>
          <div class="layer-tree-main">
            ${isSounding
              ? `<button class="btn ghost layer-tree-name-btn" data-act="snd-select" data-id="${esc(l.id)}">${esc(l.name)}</button>`
              : `<div class="layer-tree-name" title="${esc(l.name)}">${esc(l.name)}</div>`}
            <div class="layer-tree-meta">${esc(l.kind || "layer")}${Number.isFinite(count) ? ` - ${count}` : ""}${src ? ` - ${esc(src.toUpperCase())}` : ""}</div>
          </div>
          <input class="layer-tree-opacity" type="range" min="0" max="1" step="0.01" data-act="opacity" data-id="${esc(l.id)}" value="${Number(l.opacity) || 1}" title="Opacity" />
          <div class="layer-tree-opacity-label">${Math.round((Number(l.opacity) || 1) * 100)}%</div>
          ${tracklineStyleControls(l)}
          ${hasLegend ? `<button class="iconbtn ${legendVisible ? "active" : ""}" data-act="legend" data-id="${esc(l.id)}" title="${legendVisible ? "Hide color scale" : "Show color scale"}">L</button>` : ""}
          <button class="iconbtn" data-act="zoom" data-id="${esc(l.id)}" title="Zoom to">⌖</button>
          <button class="iconbtn danger" data-act="remove" data-id="${esc(l.id)}" title="Remove">x</button>
        </div>
      `;
    }

    function render() {
      const currentlyOpen = new Set(
        [...containerEl.querySelectorAll("details[data-cat][open]")]
          .map((d) => d.getAttribute("data-cat"))
          .filter(Boolean),
      );
      currentlyOpen.forEach((k) => openSet.add(k));

      const grouped = groupLayers();
      const types = orderedTypes(grouped).filter((name) => (grouped.get(name) || []).length > 0);
      if (firstRender && openSet.size === 0) {
        types.forEach((k) => openSet.add(k));
        writeOpenSet(openSet);
      }

      const sections = types.map((name) => {
        const layers = grouped.get(name) || [];
        return `
          <details class="toolset-group layer-tree-group" data-cat="${esc(name)}" ${openSet.has(name) ? "open" : ""}>
            <summary><span>${esc(name)}</span><span class="small">${layers.length}</span></summary>
            <div class="toolset-content layer-tree-items">
              ${layers.map(row).join("")}
              <div class="layer-tree-drop-end" data-layer-drop-end="1"></div>
            </div>
          </details>
        `;
      }).join("");

      const unresolvedBanner = unresolvedCount > 0
        ? `<div class="small" style="margin-bottom:8px; padding:6px 8px; border:1px solid rgba(251,191,36,.35); border-radius:8px; background:rgba(120,53,15,.22);">Unresolved sources: <strong>${unresolvedCount}</strong> (use Settings -> Project to relink)</div>`
        : "";
      containerEl.innerHTML = `${unresolvedBanner}${sections || `<div class="small">No layers in global registry.</div>`}`;
      firstRender = false;
    }

    function onClick(e) {
      const btn = e.target?.closest?.("[data-act]");
      if (!btn) return;
      const act = btn.getAttribute("data-act");
      const id = btn.getAttribute("data-id");
      if (!id) return;
      if (act === "remove") app.services.layers.removeLayer(id);
      else if (act === "zoom") app.services.layers.zoomTo(id);
      else if (act === "legend") {
        const rec = app.services.layers.listLayers().find((x) => x.id === id);
        const next = !(rec?.meta?.legendVisible === true);
        app.services.layers.updateLayer(id, { meta: { legendVisible: next } });
      }
      else if (act === "snd-select") app.events.emit("soundings:active-layer:set", { globalLayerId: id });
      else if (act === "trackline-weight-set" || act === "trackline-line-type-set") {
        const rec = app.services.layers.listLayers().find((x) => x.id === id);
        const menu = btn.closest("details.trackline-style-menu");
        if (menu) menu.open = false;
        app.services.layers.updateLayer(id, {
          meta: {
            tracklineColor: rec?.meta?.tracklineColor,
            tracklineWeight: act === "trackline-weight-set" ? Number(btn.getAttribute("data-value") || 3) : rec?.meta?.tracklineWeight,
            tracklineLineType: act === "trackline-line-type-set" ? String(btn.getAttribute("data-value") || "solid") : rec?.meta?.tracklineLineType,
            tracklineColorMode: rec?.meta?.tracklineColorMode,
          },
        });
      }
      else if (act === "rename") {
        const current = app.services.layers.listLayers().find((x) => x.id === id)?.name || "";
        const next = window.prompt("Layer name", current);
        const trimmed = String(next || "").trim();
        if (!trimmed) return;
        app.services.layers.setName(id, trimmed);
        app.events.emit("soundings:layer:rename", { globalLayerId: id, name: trimmed });
      }
    }

    function onChange(e) {
      const el = e.target;
      const id = el?.dataset?.id;
      const act = el?.dataset?.act;
      if (!id || !act) return;
      if (act === "vis") app.services.layers.setVisible(id, !!el.checked);
      if (act === "opacity") app.services.layers.setOpacity(id, Number(el.value));
      if (act === "trackline-color") {
        const rec = app.services.layers.listLayers().find((x) => x.id === id);
        app.services.layers.updateLayer(id, {
          meta: {
            tracklineColor: String(el.value || "#2dd4bf"),
            tracklineWeight: rec?.meta?.tracklineWeight,
            tracklineLineType: rec?.meta?.tracklineLineType,
            tracklineColorMode: rec?.meta?.tracklineColorMode,
          },
        });
      }
      if (act === "trackline-color-mode") {
        const rec = app.services.layers.listLayers().find((x) => x.id === id);
        app.services.layers.updateLayer(id, {
          meta: {
            tracklineColor: rec?.meta?.tracklineColor,
            tracklineWeight: rec?.meta?.tracklineWeight,
            tracklineLineType: rec?.meta?.tracklineLineType,
            tracklineColorMode: String(el.value || "solid"),
          },
        });
      }
    }

    function onInput(e) {
      const el = e.target;
      const id = el?.dataset?.id;
      const act = el?.dataset?.act;
      if (!id || act !== "opacity") return;
      const value = Number(el.value);
      app.services.layers.setOpacity(id, value, { emit: false, persist: false });
      const rowEl = el.closest(".layer-tree-row");
      const pctEl = rowEl?.querySelector?.(".layer-tree-opacity-label");
      if (pctEl) pctEl.textContent = `${Math.round((Number.isFinite(value) ? value : 1) * 100)}%`;
    }

    function onToggle(e) {
      const d = e.target;
      if (!d?.matches?.("details[data-cat]")) return;
      const cat = d.getAttribute("data-cat");
      if (!cat) return;
      if (d.open) openSet.add(cat); else openSet.delete(cat);
      writeOpenSet(openSet);
    }

    function animateRowsReflow(contentEl, mutateDom) {
      if (!contentEl || typeof mutateDom !== "function") return;
      const rowsBefore = [...contentEl.querySelectorAll(".layer-tree-row[data-layer-id]")];
      const firstTop = new Map(rowsBefore.map((el) => [String(el.getAttribute("data-layer-id") || ""), el.getBoundingClientRect().top]));
      mutateDom();
      const rowsAfter = [...contentEl.querySelectorAll(".layer-tree-row[data-layer-id]")];
      rowsAfter.forEach((el) => {
        const id = String(el.getAttribute("data-layer-id") || "");
        const first = firstTop.get(id);
        if (!Number.isFinite(first)) return;
        const last = el.getBoundingClientRect().top;
        const delta = first - last;
        if (Math.abs(delta) < 0.5) return;
        el.style.transition = "none";
        el.style.transform = `translateY(${delta}px)`;
        requestAnimationFrame(() => {
          el.style.transition = "";
          el.style.transform = "";
        });
      });
    }

    function ensureDragPlaceholder() {
      if (dragPlaceholderEl) return dragPlaceholderEl;
      const el = document.createElement("div");
      el.className = "layer-tree-drop-placeholder";
      dragPlaceholderEl = el;
      return dragPlaceholderEl;
    }

    function clearDragVisuals() {
      containerEl.querySelectorAll(".layer-tree-row-dragging,.layer-tree-row-drop-before,.layer-tree-row-drop-after").forEach((el) => {
        el.classList.remove("layer-tree-row-dragging", "layer-tree-row-drop-before", "layer-tree-row-drop-after");
      });
      dragSourceEl?.classList?.remove("layer-tree-row-drag-source-hidden", "layer-tree-row-drag-source-collapsed");
      if (dragGhostEl?.parentElement) dragGhostEl.parentElement.removeChild(dragGhostEl);
      dragGhostEl = null;
      if (dragPlaceholderEl?.parentElement) dragPlaceholderEl.parentElement.removeChild(dragPlaceholderEl);
      dragPlaceholderEl = null;
      dragSourceEl = null;
      dragLayerId = "";
      dragLayerCat = "";
      lastDropAnchorKey = "";
      isLayerDragging = false;
      if (pendingRender) {
        pendingRender = false;
        render();
      }
    }

    function onDragStart(e) {
      const rowEl = e.target?.closest?.(".layer-tree-row[data-layer-id]");
      if (!rowEl) return;
      if (suppressDrag || e.target?.closest?.("input,button,select,textarea,label")) {
        e.preventDefault();
        return;
      }
      dragLayerId = String(rowEl.getAttribute("data-layer-id") || "");
      dragLayerCat = String(rowEl.getAttribute("data-layer-cat") || "");
      if (!dragLayerId || !dragLayerCat) return;
      isLayerDragging = true;
      dragSourceEl = rowEl;
      rowEl.classList.add("layer-tree-row-dragging");
      const placeholder = ensureDragPlaceholder();
      const h = Math.max(28, Math.round(rowEl.getBoundingClientRect().height || 32));
      placeholder.style.height = `${h}px`;
      rowEl.insertAdjacentElement("afterend", placeholder);
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", dragLayerId);
        const rect = rowEl.getBoundingClientRect();
        const ghost = rowEl.cloneNode(true);
        ghost.classList.add("layer-tree-drag-ghost");
        ghost.style.width = `${Math.round(rect.width)}px`;
        ghost.style.height = `${Math.round(rect.height)}px`;
        ghost.style.position = "fixed";
        ghost.style.left = "-10000px";
        ghost.style.top = "-10000px";
        ghost.style.pointerEvents = "none";
        document.body.appendChild(ghost);
        dragGhostEl = ghost;
        const offsetX = Math.max(8, Math.min(rect.width - 8, (e.clientX || rect.left) - rect.left));
        const offsetY = Math.max(8, Math.min(rect.height - 8, (e.clientY || rect.top) - rect.top));
        e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
      } catch {}
      requestAnimationFrame(() => {
        if (!dragSourceEl || dragSourceEl !== rowEl || !isLayerDragging) return;
        rowEl.classList.add("layer-tree-row-drag-source-collapsed");
      });
    }

    function onDragOver(e) {
      if (!dragLayerId || !dragLayerCat) return;
      const group = e.target?.closest?.("details[data-cat]");
      if (!group) return;
      if (String(group.getAttribute("data-cat") || "") !== dragLayerCat) return;
      const content = group.querySelector(".layer-tree-items");
      if (!content) return;
      e.preventDefault();
      const placeholder = ensureDragPlaceholder();
      const rows = [...content.querySelectorAll('.layer-tree-row[data-layer-id]')]
        .filter((r) => r.getAttribute("data-layer-id") !== dragLayerId);
      if (!rows.length) {
        if (lastDropAnchorKey !== "empty") {
          lastDropAnchorKey = "empty";
          animateRowsReflow(content, () => {
            content.insertBefore(placeholder, content.querySelector("[data-layer-drop-end='1']") || null);
          });
        }
        return;
      }
      let insertIndex = rows.length;
      for (let i = 0; i < rows.length; i += 1) {
        const rect = rows[i].getBoundingClientRect();
        const middleY = rect.top + (rect.height / 2);
        if (e.clientY < middleY) {
          insertIndex = i;
          break;
        }
      }
      const nextKey = `idx:${insertIndex}`;
      if (nextKey === lastDropAnchorKey) return;
      lastDropAnchorKey = nextKey;
      if (insertIndex >= rows.length) {
        animateRowsReflow(content, () => {
          rows[rows.length - 1].insertAdjacentElement("afterend", placeholder);
        });
      } else {
        animateRowsReflow(content, () => {
          rows[insertIndex].insertAdjacentElement("beforebegin", placeholder);
        });
      }
    }

    function onDrop(e) {
      if (!dragLayerId || !dragLayerCat) return;
      const group = e.target?.closest?.("details[data-cat]");
      if (!group) return;
      if (String(group.getAttribute("data-cat") || "") !== dragLayerCat) return;
      const content = group.querySelector(".layer-tree-items");
      if (!content) return;
      e.preventDefault();
      const orderedIds = [...content.querySelectorAll(".layer-tree-row[data-layer-id]")]
        .map((el) => String(el.getAttribute("data-layer-id") || ""))
        .filter((id) => id && id !== dragLayerId);
      const grouped = groupLayers();
      const catIds = (grouped.get(dragLayerCat) || []).map((l) => String(l.id || "")).filter(Boolean);
      if (!catIds.length) {
        clearDragVisuals();
        return;
      }
      const deduped = [];
      orderedIds.forEach((id) => { if (!deduped.includes(id)) deduped.push(id); });
      if (!deduped.includes(dragLayerId)) {
        const orderNodes = [...content.children]
          .filter((el) => el.matches?.(".layer-tree-row[data-layer-id], .layer-tree-drop-placeholder"));
        const phIdx = orderNodes.findIndex((el) => el.classList?.contains("layer-tree-drop-placeholder"));
        const rowBefore = orderNodes.slice(0, phIdx).filter((el) => el.matches?.(".layer-tree-row[data-layer-id]")).length;
        deduped.splice(Math.min(Math.max(0, rowBefore), deduped.length), 0, dragLayerId);
      }
      const mergedTopToBottom = [...deduped, ...catIds.filter((id) => !deduped.includes(id))];
      app.services.layers.reorderSubset(mergedTopToBottom);
      clearDragVisuals();
    }

    function onDragEnd() {
      clearDragVisuals();
    }

    function onPointerDown(e) {
      const interactive = e.target?.closest?.("input,button,select,textarea,label,.iconbtn,.layer-tree-opacity,.layer-tree-opacity-label");
      suppressDrag = !!interactive;
    }

    function onPointerUp() {
      suppressDrag = false;
    }

    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("toggle", onToggle);
    containerEl.addEventListener("dragstart", onDragStart);
    containerEl.addEventListener("dragover", onDragOver);
    containerEl.addEventListener("drop", onDrop);
    containerEl.addEventListener("dragend", onDragEnd);
    containerEl.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("pointercancel", onPointerUp, true);
    off.push(app.events.on("layers:changed", () => {
      if (isLayerDragging) {
        pendingRender = true;
        return;
      }
      render();
    }));
    off.push(app.events.on("soundings:active-layer:changed", ({ globalLayerId } = {}) => {
      activeSoundingsGlobalId = globalLayerId || null;
      render();
    }));
    off.push(app.events.on("project:restore:report", (report = {}) => {
      unresolvedCount = Array.isArray(report?.unresolved) ? report.unresolved.length : 0;
      render();
    }));
    render();

    const cleanup = () => {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("toggle", onToggle);
      containerEl.removeEventListener("dragstart", onDragStart);
      containerEl.removeEventListener("dragover", onDragOver);
      containerEl.removeEventListener("drop", onDrop);
      containerEl.removeEventListener("dragend", onDragEnd);
      containerEl.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointercancel", onPointerUp, true);
      off.splice(0).forEach((fn) => { try { fn?.(); } catch {} });
    };
    containerEl.__globalLayersCleanup?.();
    containerEl.__globalLayersCleanup = cleanup;
  },
};
